import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCookieBite, FaTimes, FaCheck } from 'react-icons/fa';
import { useLanguage } from '../i18n';

const CookieConsent = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay before showing the banner
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const closeBanner = () => {
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto bg-gray-900/95 backdrop-blur-lg border border-gray-700 rounded-2xl shadow-2xl overflow-hidden">
          {/* Main Banner */}
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <FaCookieBite className="text-white text-2xl" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white mb-2">
                  {t('cookies.banner.title')}
                </h3>
                <p className="text-gray-300 text-sm mb-4">
                  {t('cookies.banner.description')}
                </p>

                {/* Links */}
                <div className="flex flex-wrap gap-4 text-sm mb-4">
                  <Link
                    to="/privacy-policy"
                    className="text-orange-400 hover:text-orange-300 underline transition-colors"
                  >
                    {t('cookies.banner.privacyPolicy')}
                  </Link>
                  <Link
                    to="/cookie-policy"
                    className="text-orange-400 hover:text-orange-300 underline transition-colors"
                  >
                    {t('cookies.banner.cookiePolicy')}
                  </Link>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={acceptAll}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-orange-500/30 transition-all transform hover:scale-[1.02]"
                  >
                    <FaCheck className="text-sm" />
                    {t('cookies.banner.acceptAll')}
                  </button>
                  <button
                    onClick={acceptNecessary}
                    className="px-5 py-2.5 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    {t('cookies.banner.acceptNecessary')}
                  </button>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="px-5 py-2.5 text-gray-400 font-medium hover:text-white transition-colors"
                  >
                    {showDetails ? t('cookies.banner.hideDetails') : t('cookies.banner.showDetails')}
                  </button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={closeBanner}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-white transition-colors"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Details Section */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <div className="grid gap-4 md:grid-cols-3">
                      {/* Necessary Cookies */}
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white">{t('cookies.types.necessary.title')}</h4>
                          <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                            {t('cookies.types.always')}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          {t('cookies.types.necessary.description')}
                        </p>
                      </div>

                      {/* Analytics Cookies */}
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white">{t('cookies.types.analytics.title')}</h4>
                          <span className="text-xs text-orange-400 bg-orange-500/20 px-2 py-1 rounded">
                            {t('cookies.types.optional')}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          {t('cookies.types.analytics.description')}
                        </p>
                      </div>

                      {/* Marketing Cookies */}
                      <div className="bg-gray-800/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white">{t('cookies.types.marketing.title')}</h4>
                          <span className="text-xs text-orange-400 bg-orange-500/20 px-2 py-1 rounded">
                            {t('cookies.types.optional')}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          {t('cookies.types.marketing.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
